import { env } from '../../api';

export async function delay(millis: number) {
  await new Promise((resolve) => setTimeout(resolve, millis));
}

export const getDateOffset = (date: Date) => {
  return (
    date.getHours() * 60 * 60 * 1000 + date.getMinutes() * 60 * 1000 + date.getSeconds() * 1000 + date.getMilliseconds()
  );
};

export const isMultipool = () => {
  return env.poolType === 'multi';
};

export enum HashUnit {
  Hash = 'Hash',
  KH = 'kH',
  MH = 'MH',
  GH = 'GH',
  TH = 'TH',
  PH = 'PH',
  EH = 'EH',
}

export const convertHash = (hash: number, target: HashUnit) => {
  const sizes = {
    [HashUnit.Hash]: 0,
    [HashUnit.KH]: 1,
    [HashUnit.MH]: 2,
    [HashUnit.GH]: 3,
    [HashUnit.TH]: 4,
    [HashUnit.PH]: 5,
    [HashUnit.EH]: 6,
  };
  return hash === 0 ? hash : hash / Math.pow(1000, sizes[target]);
};

export const stableHash = (hash: number) => {
  const hashUnits = [HashUnit.EH, HashUnit.PH, HashUnit.TH, HashUnit.GH, HashUnit.MH, HashUnit.KH, HashUnit.Hash];

  for (const unit of hashUnits) {
    const convertedHash = convertHash(hash, unit);
    if (convertedHash > 1) {
      return convertedHash;
    }
  }

  return hash;
};

export const stableHashUnit = (hash: number) => {
  const hashUnits = [HashUnit.EH, HashUnit.PH, HashUnit.TH, HashUnit.GH, HashUnit.MH, HashUnit.KH, HashUnit.Hash];

  for (const unit of hashUnits) {
    const convertedHash = convertHash(hash, unit);
    if (convertedHash > 1) {
      return unit;
    }
  }

  return HashUnit.Hash;
};

export enum ByteSizeUnit {
  Bytes = 'Bytes',
  KB = 'KB',
  MB = 'MB',
  GB = 'GB',
  TB = 'TB',
}

export const convertBytes = (bytes: number, target: ByteSizeUnit) => {
  const sizes = {
    [ByteSizeUnit.Bytes]: 0,
    [ByteSizeUnit.KB]: 1,
    [ByteSizeUnit.MB]: 2,
    [ByteSizeUnit.GB]: 3,
    [ByteSizeUnit.TB]: 4,
  };
  return bytes === 0 ? bytes : bytes / Math.pow(1024, sizes[target]);
};

export const formatThoundsandNumber = (num: number, maximumFractionDigits: number = 0) => {
  return num.toLocaleString('en-US', { maximumFractionDigits });
};

export const pruneCenter = (str: string, pruneLeftLength: number) => {
  if (str.length <= pruneLeftLength) {
    return str;
  }
  const eachPartLength = Math.round(pruneLeftLength / 2);
  return str.slice(0, eachPartLength) + '...' + str.slice(-eachPartLength);
};

export const hexToDecimal = (hexString: string) => {
  // Remove leading '0x' if present
  if (hexString.startsWith('0x')) {
    hexString = hexString.substring(2);
  }

  // Convert hexadecimal string to decimal number
  const decimalNumber = parseInt(hexString, 16);

  return decimalNumber;
};

export const formatNumber = (
  num: number | string,
  option?: { minimumFractionDigits?: number; maximumFractionDigits?: number; useGrouping?: boolean; locate?: string }
) =>
  Number(num).toLocaleString(option?.locate || 'en-US', {
    useGrouping: option?.useGrouping || true,
    minimumFractionDigits: option?.minimumFractionDigits || 0,
    maximumFractionDigits: option?.maximumFractionDigits || 18,
  });

export const formatBalanceToAbel = (balance: number, maximumFractionDigits: number = 7) =>
  (balance / 1e7).toLocaleString('en-US', { maximumFractionDigits });

export enum Currency {
  Abel = 'ABEL',
  Neutrinos = 'Neutrinos',
}

export const convertAbelCurrency = (amount: number, from: Currency, to: Currency) => {
  if (from === Currency.Abel && to === Currency.Neutrinos) {
    return amount * 1e7;
  } else if (from === Currency.Neutrinos && to === Currency.Abel) {
    return amount / 1e7;
  }
  return amount;
};

export const dynamicallyCompare = (a: string | number, b: string | number) => {
  if (typeof a !== typeof b) throw new Error('Enfore to same type first!');

  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return (a as string).localeCompare(b as string);
};
